import React, { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import {
  Typography,
  Button,
  Paper,
  Box,
  TextField,
  Switch,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomLoading from "../../components/common/CustomLoading";
import apiService, {
  type GetReferralSettingsApiEnvelope,
  type ReferralSettingsDto,
  type SaveReferralSettingsApiEnvelope,
} from "../../services/api";
import { useNotification } from "../../contexts/NotificationContext";

const primaryBlue = "rgba(22, 67, 135, 1)";
/** Matches EditHubPage primary actions */
const hubActionBlue = "rgba(13,44,90,1)";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#dee2e6" },
  },
};

const sectionTitleSx = {
  color: primaryBlue,
  fontWeight: 700,
  fontSize: "1.1rem",
  mb: 2.5,
};

function referralEnvelopeSucceeded(env: GetReferralSettingsApiEnvelope): boolean {
  if (env.isSuccess === true || env.success === true) return true;
  if (typeof env.statusCode === "number" && env.statusCode >= 200 && env.statusCode < 300) {
    return env.data != null;
  }
  return false;
}

function saveReferralEnvelopeSucceeded(env: SaveReferralSettingsApiEnvelope): boolean {
  if (env.isSuccess === true || env.success === true) return true;
  if (typeof env.statusCode === "number" && env.statusCode >= 200 && env.statusCode < 300) {
    return true;
  }
  return false;
}

function parseRewardAmount(
  input: string,
  label: string,
): { ok: true; value: number } | { ok: false; error: string } {
  const t = input.trim();
  if (t === "") return { ok: true, value: 0 };
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) {
    return { ok: false, error: `Enter a valid number for ${label}.` };
  }
  if (n < 0) {
    return { ok: false, error: `${label} cannot be negative.` };
  }
  return { ok: true, value: n };
}

function pickReferralDto(env: GetReferralSettingsApiEnvelope): ReferralSettingsDto | null {
  const d = env.data;
  if (d != null && typeof d === "object") return d;
  return null;
}

function rewardToInputString(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string") {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? String(n) : "";
  }
  if (typeof v === "bigint") return String(Number(v));
  return "";
}

function enableFromDto(d: ReferralSettingsDto): boolean {
  const raw: unknown = d.enablereferral ?? d.EnableReferral;
  if (raw === undefined) return true;
  if (typeof raw === "boolean") return raw;
  if (raw === 1 || raw === "1") return true;
  if (raw === 0 || raw === "0") return false;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
  }
  return true;
}

const ReferralSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [referrerReward, setReferrerReward] = useState("");
  const [refereeReward, setRefereeReward] = useState("");
  const [enableReferralProgram, setEnableReferralProgram] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const applySettingsFromApi = useCallback((env: GetReferralSettingsApiEnvelope) => {
    const dto = pickReferralDto(env);
    if (!dto) {
      setLoadError(env.message ?? "No referral settings returned.");
      return;
    }
    setReferrerReward(rewardToInputString(dto.referrerreward ?? dto.ReferrerReward));
    setRefereeReward(rewardToInputString(dto.refereereward ?? dto.RefereeReward));
    setEnableReferralProgram(enableFromDto(dto));
    setLoadError(null);
  }, []);

  const fetchSettings = useCallback(
    async (options?: { withPageSpinner?: boolean }) => {
      const withSpinner = options?.withPageSpinner === true;
      if (withSpinner) setInitialLoading(true);
      setLoadError(null);
      try {
        const res: GetReferralSettingsApiEnvelope = await apiService.getReferralSettings();
        if (!referralEnvelopeSucceeded(res)) {
          setLoadError(
            typeof res.message === "string" && res.message.trim() !== ""
              ? res.message.trim()
              : "Could not load referral settings.",
          );
          return;
        }
        applySettingsFromApi(res);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load referral settings.";
        setLoadError(msg);
      } finally {
        if (withSpinner) setInitialLoading(false);
      }
    },
    [applySettingsFromApi],
  );

  useEffect(() => {
    void fetchSettings({ withPageSpinner: true });
  }, [fetchSettings]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleUpdate = async () => {
    const rRef = parseRewardAmount(referrerReward, "Referrer reward");
    const rFee = parseRewardAmount(refereeReward, "Referee reward");
    if (!rRef.ok) {
      showNotification(rRef.error, "error");
      return;
    }
    if (!rFee.ok) {
      showNotification(rFee.error, "error");
      return;
    }

    setLoading(true);
    try {
      const res: SaveReferralSettingsApiEnvelope = await apiService.saveReferralSettings({
        referrerreward: rRef.value,
        refereereward: rFee.value,
        enablereferral: enableReferralProgram,
      });
      if (!saveReferralEnvelopeSucceeded(res)) {
        showNotification(
          typeof res.message === "string" && res.message.trim() !== ""
            ? res.message.trim()
            : "Could not save referral settings.",
          "error",
        );
        return;
      }
      const okMsg =
        typeof res.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : "Referral settings saved.";
      showNotification(okMsg, "success");
      await fetchSettings();
    } catch (e) {
      showNotification(e instanceof Error ? e.message : "Failed to save referral settings.", "error");
    } finally {
      setLoading(false);
    }
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: primaryBlue },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: primaryBlue,
    },
  };

  if (initialLoading) {
    return <CustomLoading message="Loading referral settings..." />;
  }

  return (
    <>
      {loadError ? (
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
            {loadError}
          </Alert>
          <Button variant="outlined" onClick={() => void fetchSettings({ withPageSpinner: true })} sx={{ mb: 2 }}>
            Retry
          </Button>
        </Box>
      ) : null}
      <Container fluid className="px-0">
        <Paper
          className="border-0 rounded-0 shadow-none"
          sx={{ px: { xs: 2, md: 4 }, py: 3 }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              borderColor: "#e0e0e0",
              backgroundColor: "#fff",
            }}
          >
            <Typography component="h2" sx={sectionTitleSx}>
              Reward Configuration
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                  Referrer Reward
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={referrerReward}
                  onChange={(e) => setReferrerReward(e.target.value)}
                  placeholder=""
                  sx={textFieldSx}
                  helperText="Amount credited to the existing user when their friend completes their first ride."
                  FormHelperTextProps={{
                    sx: { mt: 0.75, mx: 0, fontSize: "0.8125rem", lineHeight: 1.4 },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                  Referee Reward
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={refereeReward}
                  onChange={(e) => setRefereeReward(e.target.value)}
                  placeholder=""
                  sx={textFieldSx}
                  helperText="Amount credited to the new user immediately upon successful signup."
                  FormHelperTextProps={{
                    sx: { mt: 0.75, mx: 0, fontSize: "0.8125rem", lineHeight: 1.4 },
                  }}
                />
              </Box>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              borderColor: "#e0e0e0",
              backgroundColor: "#fff",
            }}
          >
            <Typography component="h2" sx={sectionTitleSx}>
              Program Controls
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "flex-start" },
                justifyContent: "space-between",
                gap: 2,
                py: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, pr: { sm: 2 } }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                  Enable Referral Program
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
                  Master switch to pause or resume the entire referral system. Existing codes will still be
                  valid but rewards won&apos;t trigger.
                </Typography>
              </Box>
              <Switch
                checked={enableReferralProgram}
                onChange={(_, v) => setEnableReferralProgram(v)}
                color="primary"
                sx={{ ...switchSx, alignSelf: { xs: "flex-end", sm: "center" }, flexShrink: 0 }}
              />
            </Box>
          </Paper>
        </Paper>
      </Container>
      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          className="px-2 py-2 fw-medium text-white"
          onClick={() => void handleUpdate()}
          disabled={loading}
          sx={{
            backgroundColor: hubActionBlue,
            minWidth: "130px",
            "&:disabled": {
              backgroundColor: "rgba(13,44,90,0.5)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Updating...
            </Box>
          ) : (
            "Update Setting"
          )}
        </Button>
        <Button
          variant="outlined"
          className="px-4 py-2 fw-medium"
          onClick={handleCancel}
          disabled={loading}
          sx={{
            borderColor: hubActionBlue,
            color: hubActionBlue,
            "&:hover": {
              borderColor: hubActionBlue,
              backgroundColor: "rgba(13,44,90,0.04)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default ReferralSettingsPage;
